<template>
  <q-page padding class="text-center">
    <h2>Import / Export Products</h2>
    <div>
      Be real careful using this functionality.
    </div>
    <h4 class="q-mb-sm">1. Download Products</h4>
    <div>
      <q-btn class="q-ma-md" @click="downloadProductsCSV">Download All Products CSV</q-btn>
    </div>
    <h4 class="q-mb-sm">2. Edit Downloaded File making necessary changes</h4>
    <h4 class="q-mb-sm">3. Upload Colours and Edited csv from step 1</h4>
    <div class="full-width row flex-center text-accent q-gutter-sm">
      <q-uploader
        ref="coloursUploader"
        @added="uploaderFactoryColours"
        label="Colours Csv"
        :max-file-size="10000000"
        accept=".csv"
        hide-upload-btn
        style="max-width: 300px"
      />
      <q-uploader
        ref="productsUploader"
        @added="uploaderFactory"
        label="Edited Products Csv"
        :max-file-size="50000000"
        accept=".csv"
        hide-upload-btn
        style="max-width: 300px"
      />
    </div>
    <div class="q-pt-md">{{summaryMessage}}</div>

    <h4 class="q-mb-sm">4. Fix These Errors</h4>
    <div>
      <div :key="error" v-for="error in validationErrors" style="color: red;">{{error}}</div>
    </div>

    <h4 class="q-mb-sm">5. Click This Button</h4>
    <q-btn :disabled="validationErrors.length > 0 || coloursCsvData === null || productsCsvData === null || performingUpdate" class="q-ma-md" color="negative" @click="replaceAllProducts">Replace All Products From CSV</q-btn>
    <div>{{response}}</div>
<!--    Disabled this until backup restoration is working reliably. -->
<!--    <q-btn v-if="response && hasBackupAccess" icon="undo" @click="undo">Undo</q-btn>-->
  </q-page>
</template>

<script>
import gql from 'graphql-tag'
import { csv } from 'csvtojson'
import saveAs from 'file-saver'
import adminNotifyError from '../../util/adminNotifyError'
import moment from 'moment'

export default {
  name: 'PageAdminDashboard',
  computed: {
    hasBackupAccess () {
      return this.currentAccount && this.currentAccount.roles.includes('backupAccess')
    },
    hasBulkProductAccessRole () {
      return this.currentAccount && this.currentAccount.roles.includes('bulkProductsAccess')
    },
    currentAccount () {
      return this.$store.state.auth.currentAccount
    }
  },
  methods: {
    async downloadProductsCSV () {
      const result = await this.$apollo.query({
        query: gql`query productsCsv {
          productsCsv
        }`,
        fetchPolicy: 'network-only'
      })
      saveAs(new Blob([result.data.productsCsv], { type: 'application/octet-stream' }), `ICP_Products.csv`)
    },
    replaceAllProducts () {
      this.$q.dialog({
        title: 'Upload Products',
        message: `Are you sure you want to upload these products?`,
        cancel: true
      }).onOk(() => {
        this.$q.dialog({
          title: 'Are You Sure?',
          message: `This will completely replace the products on the site.`,
          cancel: true
        }).onOk(() => {
          this.replaceAllProductsWithoutAsking()
        })
      })
    },
    async replaceAllProductsWithoutAsking () {
      if (this.validationErrors.length > 0 || this.coloursCsvData === null || this.productsCsvData === null) return
      if (this.performingUpdate) return
      this.performingUpdate = true
      try {
        const response = await this.$apollo.mutate({
          mutation: gql`
            mutation restoreProductsCsv ($productsCsv: String!, $colourCsv: String) {
                restoreProductsCsv(productCsv: $productsCsv, colourCsv: $colourCsv)
          }`,
          variables: {
            productsCsv: this.productsCsvData,
            colourCsv: this.coloursCsvData
          }
        })

        this.response = `Deleted all products then added ${response.data.restoreProductsCsv} products.  A backup was created prior.`

        this.$refs.productsUploader.reset()
        this.$refs.coloursUploader.reset()
        this.productsCsvData = null
        this.coloursCsvData = null
      } catch (error) {
        adminNotifyError(this.$q, 'Could not replace all products.', error)
      }
      this.performingUpdate = false
    },
    async validate () {
      try {
        const errors = []
        let productCount = 0
        let skuCount = 0
        let productsData = await csv().fromString(this.productsCsvData)
        let colourData = await csv().fromString(this.coloursCsvData)
        const skuNumbers = Object.keys(productsData[0])
          .filter(k => k.startsWith('sku') && k.endsWith('colour'))
          .map(header => header.split('_')[0].substr(3))
        const imageNumbers = Object.keys(productsData[0])
          .filter(k => k.startsWith('image'))
          .map(header => header.replace('image', ''))
        for (const product of productsData) {
          for (const skuNumber of skuNumbers) {
            const skuColour = product[`sku${skuNumber}_colour`]
            if (!skuColour) continue
            const matchedColour = colourData.find(colour => colour.Name === skuColour)
            if (!matchedColour) {
              errors.push(`Brand: '${product['brand']}' Ref: '${product['reference']}' sku${skuNumber} colour '${skuColour}' not in colour table.`)
            }
            skuCount += 1
          }
          for (const imageNumber of imageNumbers) {
            const imagePath = product[`image${imageNumber}`]
            if (!imagePath) continue
            if (imagePath.startsWith('https://') || imagePath.startsWith('http://') || imagePath.startsWith('www.' || imagePath.startsWith('//'))) {
              errors.push(`${product['brand']} ${product['reference']} image${imageNumber} '${imagePath}' is not a local image.`)
            }
          }
          productCount += 1
        }
        this.$set(this, 'validationErrors', errors)
        this.response = ''
        this.summaryMessage = `Summary: ${productCount} products and ${skuCount} skus`
      } catch (error) {

      }
    },
    async uploaderFactory (files) {
      try {
        if (!files || !files.length) throw Error('no files')
        this.productsCsvData = await files[0].text()
        this.$set(this, 'validationErrors', ['.'])
      } catch (error) {
        this.productsCsvData = null
      }
      await this.validate()
    },
    async uploaderFactoryColours (files) {
      try {
        if (!files || !files.length) throw Error('no files')
        this.coloursCsvData = await files[0].text()
      } catch (error) {
        this.coloursCsvData = null
      }
      this.validate()
    },
    async undo () {
      try {
        const result = await this.$apollo.query({
          query: gql`
            query {
              listBackups {
                name
                sizeInBytes
                date
              }
            }`,
          fetchPolicy: 'network-only'
        })
        let backups = result.data.listBackups
        let mostRecentBackup = backups.reduce((p, c) => moment(c.date) > moment(p.date) ? c : p, { date: moment().subtract(1, 'day') })
        if (!mostRecentBackup.name) throw Error(`Aborting because most recent backup is from more than ${moment(mostRecentBackup.date).fromNow()}.`)
        this.$q.dialog({
          title: 'Restore Backup',
          message: `Are you sure you want to restore the backup from ${moment(mostRecentBackup.date).fromNow()}?`,
          cancel: true
        }).onOk(async () => {
          await this.restoreBackupWithoutPrompt(mostRecentBackup.name)
          this.summaryMessage = ''
          this.response = ''
        })
      } catch (error) {
        adminNotifyError(this.$q, 'Failed to undo operation', error)
      }
    },
    async restoreBackupWithoutPrompt (backupName) {
      await this.$apollo.mutate({
        mutation: gql`mutation restoreBackup ( $backupName: String ) {
          restoreBackup ( backupName: $backupName )
        }`,
        variables: {
          backupName
        }
      })
      this.$q.dialog({
        title: 'DB Restored!',
        message: `The backup ${backupName} has been restored!`
      })
    }
  },
  data () {
    return {
      coloursCsvData: null,
      productsCsvData: null,
      validationErrors: ['.'],
      response: '',
      performingUpdate: false,
      summaryMessage: ''
    }
  }
}
</script>
